import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import Price from '@/components/ui/Price';
import { formatTicketTime } from './ticketTime';
import {
  caisseBridgeLink,
  useCancelOnlineOrderMutation,
  useFinishOnlineOrderMutation,
  useHandoffOnlineOrderToCaisseMutation,
  useLazyPrintTotalStatusQuery,
  useOnlineOrderDetailQuery,
  usePrintCapabilitiesQuery,
  usePrintOnlineOrderReceiptMutation,
  type WaiterOnlineOrder,
} from './waiterApi';
import {
  printOutcomeDestination,
  PRINT_OUTCOME_MESSAGES,
  reportPrintOutcome,
} from './printOutcome';
import KitchenReprintSheet from '@/features/waiterTicket/KitchenReprintSheet';
import KitchenIcon from '@/features/waiterTicket/KitchenIcon';
import type { ShowWaiterToast } from '@/features/waiterTicket/waiterToast';

interface Props {
  /** `null` closes the modal — same "controlled by presence" shape as
   *  `OpenTableModal`. */
  order: WaiterOnlineOrder | null;
  token: string;
  onClose: () => void;
  /** Le toast du carnet, par-dessus la feuille « Réimprimer en cuisine ».
   *  Absent, le suivi s'affiche dans la modale comme celui du reçu. */
  showToast?: ShowWaiterToast;
}

function priceToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

const CAISSE_STATE_KEYS = {
  requested: 'waiter.notes.online.caisseRequested',
  imported: 'waiter.notes.online.caisseImported',
  failed: 'waiter.notes.online.caisseRefused',
} as const;

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.3 D7 — the centred « Commande
 * en ligne » modal opened by tapping an `OnlineOrderRow`.
 *
 * SC-960 — ses actions sont désormais CELLES DE LA NOTE À EMPORTER
 * (`waiterTicket/TicketFooter.tsx`) : Partir · Imprimer · ✔, le ✔ ouvrant
 * « Envoyer en caisse » / « Terminer sans passer par la caisse ». Un serveur
 * n'a pas à connaître deux grammaires selon que la commande vient du site ou
 * de son propre carnet. « Marquer comme prêt » disparaît d'ici — la
 * préparation reste pilotée depuis les tableaux de bord, comme l'édition et le
 * refus le sont déjà (D7).
 */
export default function OnlineOrderDetailModal({ order, token, onClose, showToast }: Props) {
  const { t } = useTranslation();
  const open = order !== null;
  return (
    <Modal open={open} onClose={onClose} ariaLabel={order ? t('waiter.notes.online.modalTitle') : ''} instant>
      {order && (
        <OnlineOrderDetailModalBody order={order} token={token} onClose={onClose} showToast={showToast} />
      )}
    </Modal>
  );
}

interface BodyProps {
  order: WaiterOnlineOrder;
  token: string;
  onClose: () => void;
  showToast?: ShowWaiterToast;
}

function OnlineOrderDetailModalBody({ order, token, onClose, showToast }: BodyProps) {
  const { t } = useTranslation();
  // The row that opened this modal already carries every field but `items`
  // (`WaiterOnlineOrderDetail extends WaiterOnlineOrder`), so only the item
  // list waits on this fetch. But `order` is the tap-time snapshot — while
  // the modal stays open the 10s poll / SSE can advance the order's status
  // elsewhere (admin, kitchen), and `order` never refreshes with it (review
  // F3#2). `live` reads through to the fresh `detail` once it lands so the
  // action button / header always reflect the current state, not a stale
  // one that would 400 on tap.
  const { data: detail, isFetching: detailFetching } = useOnlineOrderDetailQuery({ orderUuid: order.uuid, token });
  const live = detail ?? order;
  const [finishOrder, { isLoading }] = useFinishOnlineOrderMutation();
  const [cancelOrder] = useCancelOnlineOrderMutation();
  const [printReceipt] = usePrintOnlineOrderReceiptMutation();
  const [fetchPrintStatus] = useLazyPrintTotalStatusQuery();
  const [error, setError] = useState<string | null>(null);
  // SC-960 — le sort du papier s'affiche ICI plutôt qu'en toast : la liste des
  // notes, qui héberge cette modale, n'en a pas, et « Imprimer » ne ferme rien.
  const [printNotice, setPrintNotice] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [finishSheetOpen, setFinishSheetOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [reprintSheetOpen, setReprintSheetOpen] = useState(false);
  // SC-948 — relu à chaque ouverture, comme le ticket serveur : le patron peut
  // lier une caisse pendant le service.
  const { data: capabilities } = usePrintCapabilitiesQuery({ token }, { skip: !token, refetchOnMountOrArgChange: true });
  const [handoffToCaisse, { isLoading: isSending }] = useHandoffOnlineOrderToCaisseMutation();

  const items = detail?.items ?? [];
  const paid = live.paymentStatus === 'PAID';
  const handoffStatus = live.caisseHandoffStatus ?? null;
  // SC-951 — une commande réglée EN LIGNE part elle aussi : la caisse la
  // journalise au libellé annoncé sans redemander l'argent. Même lecture que le
  // bouton du back-office (`canSendToCaisse`) : à encaisser au comptoir OU déjà
  // payée en ligne, et pas déjà partie — une caisse qui a refusé se relance.
  // Sur l'état relu seulement, jamais sur l'instantané de la ligne tapée.
  const settledOnline = live.paymentChannel === 'online' && paid;
  const canSendToCaisse =
    detail !== undefined &&
    capabilities?.canSendToCaisse === true &&
    ((live.paymentChannel === 'on_site' && !paid) || settledOnline) &&
    handoffStatus !== 'requested' &&
    handoffStatus !== 'imported';
  // SC-1457 — restaurant à l'ancienne caisse : même porte d'état que la caisse
  // liée, moins son accusé — le pont, lui, tient son propre registre des envois.
  const bridgeLink =
    detail !== undefined && ((live.paymentChannel === 'on_site' && !paid) || settledOnline)
      ? caisseBridgeLink(capabilities, order.uuid)
      : null;
  const busy = isLoading || isSending || printing;
  // Même porte d'état que le serveur (`WaiterOnlineOrderPrintReceiptView`) :
  // une commande annulée ou jamais payée ne sort pas un papier « A REGLER ».
  const printable = !['cancelled', 'pending_payment', 'pending_validation'].includes(live.onlineStatus);
  const canPrint = capabilities?.canPrintTotal === true && printable;
  // SC-1038 (recette QA 2026-09-22) — la toque du pied d'une table manquait
  // ici : une commande passée sur le site ne ressortait que son reçu. Même
  // porte d'état que le reçu — une commande jamais payée n'est jamais partie
  // en cuisine.
  const withKitchenReprint = capabilities?.canReprintKitchen === true && printable;

  // « Terminer sans passer par la caisse » — la commande est remise au client.
  // Elle quitte la liste du carnet et s'affiche « Remise » côté admin et owner.
  const handleFinishDirect = async () => {
    setError(null);
    try {
      await finishOrder({ orderUuid: order.uuid, token }).unwrap();
      setFinishSheetOpen(false);
      onClose();
    } catch {
      setError(t('waiter.notes.errors.generic'));
    }
  };

  // « Partir » — le client est parti : la commande est annulée, et reste
  // lisible en « Annulée » côté admin et owner avec tout son historique.
  const handleLeaveConfirm = async () => {
    setError(null);
    try {
      await cancelOrder({ orderUuid: order.uuid, token }).unwrap();
      setLeaveConfirmOpen(false);
      onClose();
    } catch (err) {
      const code = (err as { data?: { code?: string } } | undefined)?.data?.code;
      setError(
        code === 'CAISSE_HANDOFF_ACTIVE'
          ? t('waiter.ticket.errors.caisseHandoffActive')
          : t('waiter.notes.errors.generic'),
      );
    }
  };

  // « Imprimer » — le reçu sort, la commande RESTE dans la liste. Le 202 ne dit
  // que « mis en file » : le sort du papier se lit ensuite sur la ligne
  // d'attente, exactement comme pour l'addition d'une table (SC-577).
  const handlePrint = async () => {
    if (busy) return;
    setError(null);
    setPrinting(true);
    try {
      const queued = await printReceipt({ orderUuid: order.uuid, token }).unwrap();
      const destination = printOutcomeDestination(queued.destination);
      if (!destination || !queued.dispatchUuid) {
        setPrintNotice(t('waiter.overview.printTotalDone'));
      } else {
        setPrintNotice(t(PRINT_OUTCOME_MESSAGES[destination].sent));
        await reportPrintOutcome(queued.dispatchUuid, destination, {
          fetchStatus: (dispatchUuid) => fetchPrintStatus({ dispatchUuid, token }).unwrap(),
          announce: setPrintNotice,
          t,
        });
      }
    } catch (err) {
      const payload = err as { status?: number | string; data?: { code?: string } };
      setPrintNotice(null);
      if (payload?.status === 404 || payload?.data?.code === 'PRINT_EMPTY') {
        setError(t('waiter.ticket.footer.printEmpty'));
      } else if (payload?.data?.code === 'SELECT_CAISSE') {
        setError(t('waiter.overview.caisseSelectError'));
      } else {
        setError(t('waiter.notes.errors.generic'));
      }
    } finally {
      setPrinting(false);
    }
  };

  // Le 202 ne dit que « mis en file » : la modale reste ouverte et l'état relu
  // (tag `OnlineOrders`) montre la caisse attendue, puis sa réponse via le SSE.
  const handleSendToCaisse = async () => {
    setError(null);
    try {
      await handoffToCaisse({ orderUuid: order.uuid, token }).unwrap();
      // SC-960 — le ✔ se referme comme sur une note à emporter. La ligne, elle,
      // ne quitte la liste qu'à l'accusé `imported` de la caisse (SC-947) : la
      // fermer ici sur la seule mise en file ferait disparaître du mobile une
      // commande que la caisse peut encore refuser.
      setFinishSheetOpen(false);
      onClose();
    } catch (err) {
      const payload = err as { status?: number | string; data?: { code?: string; detail?: unknown } };
      if (payload?.data?.code === 'SELECT_CAISSE') {
        setError(t('waiter.overview.caisseSelectError'));
      } else if (payload?.status === 409) {
        setError(t('waiter.notes.online.caisseAlreadySent'));
      } else if (typeof payload?.data?.detail === 'string' && payload.data.detail) {
        setError(payload.data.detail);
      } else {
        setError(t('waiter.notes.errors.generic'));
      }
    }
  };

  return (
    <div
      data-testid="online-modal"
      className="p-5 flex flex-col gap-3 overflow-y-auto md:max-w-[480px] md:mx-auto md:w-full"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-sans text-[18px] font-bold">{order.consumerName}</h2>
        <button
          type="button"
          data-testid="online-close"
          aria-label={t('common.close')}
          onClick={onClose}
          className="-me-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
        >
          ×
        </button>
      </div>
      <a
        data-testid="online-phone"
        href={`tel:${order.customerPhone.replace(/\s+/g, '')}`}
        className="w-fit font-sans text-base text-ink-mute underline"
      >
        {order.customerPhone}
      </a>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-sm text-ink-mute">
        <span>
          {t('waiter.notes.online.pickupAt', {
            // Device timezone, not the restaurant's — same call-site choice
            // as `ticketTime.ts`'s header docstring: the waiter's handheld
            // is physically at the restaurant, so its local zone is the
            // display truth (review F3#6).
            time: live.scheduledAt ? formatTicketTime(live.scheduledAt) : '—',
          })}
        </span>
        {live.isAsap && (
          <span data-testid="online-asap" className="font-semibold text-ink">
            {t('checkout.slotAsap')}
          </span>
        )}
        <span>{order.type === 'delivery' ? t('waiter.notes.online.delivery') : t('waiter.notes.online.typeTakeOut')}</span>
        <span>
          {!paid
            ? t('waiter.notes.online.toCollect')
            : live.paymentChannel === 'online'
              ? t('waiter.notes.online.paidOnline')
              : // SC-948 — encaissée par la caisse (ou au comptoir) : plus rien à encaisser.
                t('waiter.notes.online.paidOnSite')}
        </span>
      </div>
      <div data-testid="online-items" className="max-h-[40dvh] overflow-y-auto flex flex-col gap-2.5">
        {items.map((item) => (
          <div key={item.uuid} className="flex flex-col">
            <div className="flex items-center justify-between gap-2 font-sans text-base">
              <span>
                {item.qte} × {item.name}
              </span>
              <Price amountCents={priceToCents(item.totalTtc)} />
            </div>
            {item.options.map((opt, idx) => (
              <p key={idx} className="ps-4 font-mono text-[15px] text-ink-mute">
                {(opt.name ?? opt.label ?? '') + (opt.qty && opt.qty > 1 ? ` ×${opt.qty}` : '')}
              </p>
            ))}
            {item.comment && <p className="ps-4 font-mono text-[15px] italic text-ink-mute">{item.comment}</p>}
          </div>
        ))}
      </div>
      {order.customerNote && (
        <div data-testid="online-customer-note" className="flex flex-col gap-1">
          <span className="font-sans text-sm font-semibold text-ink-mute">
            {t('waiter.notes.online.customerNoteLabel')}
          </span>
          <p className="font-mono text-base">{order.customerNote}</p>
        </div>
      )}
      {handoffStatus && (
        <p
          data-testid="online-caisse-state"
          role="status"
          className={`font-sans text-sm ${handoffStatus === 'failed' ? 'text-red-600' : 'text-ink-mute'}`}
        >
          {t(CAISSE_STATE_KEYS[handoffStatus])}
          {handoffStatus === 'failed' && live.caisseHandoffError && (
            <span className="block font-mono text-xs">{live.caisseHandoffError}</span>
          )}
        </p>
      )}
      {error && (
        <p data-testid="online-action-error" role="alert" className="font-mono text-sm text-red-600">
          {error}
        </p>
      )}
      {printNotice && (
        <p data-testid="online-print-state" role="status" className="font-sans text-sm text-ink-mute">
          {printNotice}
        </p>
      )}
      {/* SC-960 — même barre que le pied d'une note à emporter
          (`waiterTicket/TicketFooter.tsx`) : Partir · Imprimer · ✔. Mêmes
          gabarits, mêmes couleurs, même seuil 360px — le ✔ était coupé sous
          cette largeur quand les trois boutons portaient leur largeur mini. */}
      <div
        data-testid="online-footer"
        className={
          withKitchenReprint
            ? 'grid grid-cols-[auto_1fr_minmax(0,auto)_auto] items-center gap-2 pt-1'
            : 'grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 min-[360px]:gap-3 pt-1'
        }
      >
        <button
          type="button"
          data-testid="online-leave"
          disabled={busy}
          onClick={() => {
            setError(null);
            setLeaveConfirmOpen(true);
          }}
          className={`${withKitchenReprint ? 'px-2 min-[400px]:px-3 min-[400px]:min-w-[84px] md:min-w-[104px]' : 'px-3 min-[360px]:min-w-[104px]'} h-12 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40`}
          style={{ backgroundColor: 'var(--w-danger)' }}
        >
          {t('waiter.ticket.footer.leave')}
        </button>
        <span />
        {/* SC-1038 — la toque à GAUCHE de « Imprimer », comme au pied d'une
            table : les deux papiers se demandent au même endroit, et un appui
            manqué ne tombe jamais sur le ✔. La modale est 68px plus étroite que
            l'écran (marges + p-5) : avec la toque, les largeurs mini ne
            reviennent qu'à 400px (84/104) puis en md (104/120), l'écart reste
            8px, et « Imprimer » se tronque avant que le ✔ ne sorte (SC-761). */}
        {(withKitchenReprint || canPrint) && (
          <div className={`flex items-center gap-2${withKitchenReprint ? ' min-w-0' : ''}`}>
            {withKitchenReprint && (
              <button
                type="button"
                data-testid="online-reprint-kitchen"
                aria-label={t('waiter.ticket.commandes.reprintKitchen.aria')}
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setReprintSheetOpen(true);
                }}
                className="shrink-0 w-12 h-12 flex items-center justify-center border-2 rounded-btn disabled:opacity-40"
                style={{ borderColor: 'var(--w-info)', color: 'var(--w-info)' }}
              >
                <KitchenIcon />
              </button>
            )}
            {canPrint && (
              <button
                type="button"
                data-testid="online-print"
                disabled={busy}
                aria-busy={printing}
                onClick={() => void handlePrint()}
                className={`${withKitchenReprint ? 'min-w-0 truncate px-2 min-[400px]:px-3 min-[400px]:min-w-[104px] md:min-w-[120px]' : 'px-3 min-[360px]:min-w-[120px]'} h-12 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40`}
                style={{ backgroundColor: 'var(--w-info)' }}
              >
                {t('waiter.ticket.footer.print')}
              </button>
            )}
          </div>
        )}
        <button
          type="button"
          data-testid="online-finish"
          aria-label={t('waiter.ticket.footer.finishAria')}
          disabled={busy}
          onClick={() => {
            setError(null);
            setFinishSheetOpen(true);
          }}
          className="w-[52px] h-[52px] rounded-full flex items-center justify-center text-white text-[28px] disabled:opacity-40"
          style={{ backgroundColor: 'var(--w-success)' }}
        >
          ✔
        </button>
      </div>

      {finishSheetOpen && (
        <div
          data-testid="online-finish-sheet"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[1190] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
        >
          <div className="w-full max-w-[420px] bg-paper rounded-2xl p-5 flex flex-col gap-3">
            <p className="font-sans text-lg font-bold">
              {canSendToCaisse
                ? t('waiter.ticket.footer.finishSheet.titleCaisse')
                : t('waiter.ticket.footer.finishSheet.titleDirect')}
            </p>
            {/* SC-1457 — l'ancienne caisse : le lien ouvre le pont, qui envoie
                puis termine la commande (« Remise »). La modale se referme comme
                après l'envoi vers une caisse liée ; la ligne quitte la liste
                quand le pont l'a terminée. « Terminer » reste dessous pour un
                envoi fait à la main. */}
            {bridgeLink && (
              <a
                data-testid="online-finish-bridge"
                href={bridgeLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setFinishSheetOpen(false);
                  onClose();
                }}
                className="h-12 flex items-center justify-center rounded-btn font-sans text-[18px] font-bold text-white"
                style={{ backgroundColor: 'var(--w-info)' }}
              >
                {t('waiter.ticket.footer.finishSheet.caisse')}
              </a>
            )}
            {/* SC-1006 — avec une caisse liée, la commande part en caisse : plus de
                « Terminer sans caisse » ici. Sans caisse, « Terminer » reste le seul geste. */}
            {canSendToCaisse ? (
              <button
                type="button"
                data-testid="online-finish-caisse"
                // Tant que l'état relu n'est pas revenu, un second appui ne
                // ferait qu'un 409 : le bouton attend la relecture.
                disabled={busy || detailFetching}
                aria-busy={isSending}
                onClick={() => void handleSendToCaisse()}
                className="h-12 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--w-success)' }}
              >
                {t(
                  handoffStatus === 'failed'
                    ? 'waiter.notes.online.actionRetryCaisse'
                    : 'waiter.ticket.footer.finishSheet.caisse',
                )}
              </button>
            ) : (
              <button
                type="button"
                data-testid="online-finish-no-caisse"
                disabled={busy}
                aria-busy={isLoading}
                onClick={() => void handleFinishDirect()}
                className="h-12 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--w-success)' }}
              >
                {t('waiter.ticket.footer.finishSheet.directOnly')}
              </button>
            )}
            <button
              type="button"
              data-testid="online-finish-cancel"
              disabled={busy}
              onClick={() => setFinishSheetOpen(false)}
              className="h-12 rounded-btn font-sans text-[18px] font-bold underline disabled:opacity-40"
            >
              {t('waiter.ticket.footer.finishSheet.cancel')}
            </button>
          </div>
        </div>
      )}

      {leaveConfirmOpen && (
        <div
          data-testid="online-leave-confirm"
          role="alertdialog"
          aria-modal="true"
          className="fixed inset-0 z-[1190] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
        >
          <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm md:max-w-[420px] w-full flex flex-col gap-4">
            <p className="font-sans text-lg font-bold">{t('waiter.ticket.footer.leaveConfirm.title')}</p>
            <p className="font-sans text-[18px]">{t('waiter.notes.online.leaveBody')}</p>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="online-leave-cancel"
                disabled={busy}
                onClick={() => setLeaveConfirmOpen(false)}
                className="h-12 flex-1 border-2 border-ink rounded-btn font-sans text-[18px] font-bold disabled:opacity-40"
              >
                {t('waiter.ticket.footer.leaveConfirm.cancel')}
              </button>
              <button
                type="button"
                data-testid="online-leave-submit"
                disabled={busy}
                onClick={() => void handleLeaveConfirm()}
                className="h-12 flex-1 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--w-danger)' }}
              >
                {t('waiter.ticket.footer.leaveConfirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {reprintSheetOpen && (
        <KitchenReprintSheet
          orderUuid={order.uuid}
          token={token}
          onClose={() => setReprintSheetOpen(false)}
          showToast={showToast ?? ((message) => setPrintNotice(message))}
        />
      )}
    </div>
  );
}
